import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import DataTable, { TableStyles } from 'react-data-table-component';
import { getExceededThresholdList } from '@app/services/printReport';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAllActiveFranchise } from '@app/services/franchise';
import Select from 'react-select';
import { Button } from '@app/components';
import { setFranchisePrintSummary } from '@app/store/reducers/printSummary';
import { base64Encode } from '@app/helpers/base64url';
import ExcelJS from "exceljs/dist/es5/exceljs.browser";
import fileDownload from 'js-file-download';

const ExceededThreshold = () => {
    const selectRef = useRef(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState('id');
    const [sortDirection, setSortDirection] = useState('DESC');
    const permissions = useSelector((state: any) => state.auth.permissions);
    const canAccess = permissions?.['print-summary']?.['exceeded-threshold'];
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [franchises, setFranchises] = useState([]);

    const months = [
        {id: '01', name:'January'}, {id: '02', name:'February'}, {id: '03', name:'March'}, 
        {id: '04', name:'April'}, {id: '05', name:'May'}, {id: '06', name:'June'},
        {id: '07', name:'July'},{id: '08', name:'August'},{id: '09', name:'September'},
        {id: '10', name:'October'}, {id: '11', name:'November'}, {id: '12', name:'December'}
    ];
    const years = Array.from({ length: 5 }, (_, index) => new Date().getFullYear() - index );

    const [search, setSearch] = useState({
        franchiseId: '',
        month: '',
        year: (new Date().getFullYear()) + '',
        type: []
    });

    useEffect(() => {
        if (!canAccess) {
            navigate('/');
            toast.error('You do not have permission to access this page');
        }
    }, [canAccess, navigate]);

    useEffect(() => {
        if (canAccess) {
            getAllActiveFranchiseData();
        }
    }, [canAccess]);

    useEffect(() => {
        if (canAccess) {
            fetchExceededThreshold(currentPage, perPage, sortField, sortDirection);
        }
    }, [currentPage, perPage, sortField, sortDirection, search, canAccess]);

    const getAllActiveFranchiseData = async () => {
        try {
            const resp = await getAllActiveFranchise();
            if (resp?.success === true) {
                setFranchises(resp?.collections);
            } else {
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal server error');
        }
    };

    const onChangeFranchise = (option) => {
        if (option) {
            setSearch({ ...search, franchiseId: option.id });
        } else {
            setSearch({ ...search, franchiseId: '' });
        }
    };

    const handleResetFilter = () => {
        if (selectRef.current) {
            (selectRef.current as any).setValue('');
        }
        setSearch({
            franchiseId: '',
            month: '',
            year: (new Date().getFullYear()) + '',
            type: []
        });
        setCurrentPage(1);
    };

    const viewDetails = (e, row) => {
        e.preventDefault();
        dispatch(setFranchisePrintSummary(row));
        const encodedId = base64Encode(`${row.franchise_id}-${row.month}-${row.year}`);
        navigate(`/exceeded-print-threshold/details/${encodedId}`);
    };

    const fetchExceededThreshold = async (page, perPage, sortField, sortDirection) => {
        try {
            setLoading(true);
            const resp = await getExceededThresholdList(sortField, sortDirection, page, perPage, search.franchiseId, '', search.month, search.year, search.type);
            if (resp.success === true) {
                setData(resp.collections.data);
                setTotalRows(resp.collections.pagination.total);
                setLoading(false);
            } else {
                setLoading(false);
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal server error');
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = async (column, sortDirection) => {
        setSortField(column.sortField);
        setSortDirection(sortDirection.toUpperCase());
    };

    const handleExport = async () => {
        try {
            setLoading(true);
            
            if (!totalRows || totalRows === 0) {
                setLoading(false);
                toast.error('No records found to export');
                return;
            }

            // Fetch all records using the total count from state
            const resp = await getExceededThresholdList(sortField, sortDirection, 1, totalRows, search.franchiseId, '', search.month, search.year, search.type);
            
            if (resp.success === true) {
                setLoading(false);
                
                if (!resp.collections?.data || resp.collections.data.length === 0) {
                    toast.error('No records found to export');
                    return;
                }

                const workbook = new ExcelJS.Workbook();
                const worksheet = workbook.addWorksheet("Exceeded Threshold");
                
                // Get all unique subjects across all records
                const allSubjects = new Set();
                resp.collections.data.forEach((record) => {
                    if (record.subjects && record.subjects.length > 0) {
                        record.subjects.forEach((subject) => {
                            allSubjects.add(subject.subject_name);
                        });
                    }
                });
                
                const subjectList = Array.from(allSubjects).sort();
                
                // Build dynamic columns
                const columns = [
                    { header: "Franchise", key: "name", width: 30 },
                    { header: "Month", key: "month", width: 15 },
                    { header: "Year", key: "year", width: 10 },
                    { header: "Total Printed", key: "worksheets", width: 15 },
                    { header: "Total Exceeded", key: "total_exceeded", width: 15 }
                ];
                
                // Add dynamic subject columns
                subjectList.forEach((subjectName) => {
                    columns.push(
                        { header: `${subjectName} - Printed`, key: `${subjectName}_printed`, width: 18 },
                        { header: `${subjectName} - Threshold`, key: `${subjectName}_threshold`, width: 18 },
                        { header: `${subjectName} - Exceeded`, key: `${subjectName}_exceeded`, width: 18 }
                    );
                });
                
                worksheet.columns = columns;

                // Style header row
                worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
                worksheet.getRow(1).fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF4472C4' }
                };
                worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
                worksheet.getRow(1).height = 25;

                // Add data rows
                resp.collections.data.forEach((record, index) => {
                    const monthName = (months.find(obj => record.month === obj.id))?.name ?? record.month;
                    
                    const rowData: any = {
                        name: record.name,
                        month: monthName,
                        year: record.year,
                        worksheets: record.worksheets,
                        total_exceeded: record.total_exceeded
                    };
                    
                    // Add subject data
                    if (record.subjects && record.subjects.length > 0) {
                        record.subjects.forEach((subject) => {
                            rowData[`${subject.subject_name}_printed`] = subject.worksheet_count;
                            rowData[`${subject.subject_name}_threshold`] = subject.threshold;
                            rowData[`${subject.subject_name}_exceeded`] = subject.exceeded_count;
                        });
                    }
                    
                    const row = worksheet.addRow(rowData);
                    
                    // Alternate row colors
                    if (index % 2 === 0) {
                        row.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FFF2F2F2' }
                        };
                    }
                    
                    // Center align numeric columns
                    row.eachCell((cell, colNumber) => {
                        if (colNumber > 3) { // After Year column
                            cell.alignment = { horizontal: 'center', vertical: 'middle' };
                        }
                        cell.border = {
                            top: { style: 'thin', color: { argb: 'FFD0D0D0' } },
                            left: { style: 'thin', color: { argb: 'FFD0D0D0' } },
                            bottom: { style: 'thin', color: { argb: 'FFD0D0D0' } },
                            right: { style: 'thin', color: { argb: 'FFD0D0D0' } }
                        };
                    });
                    
                    // Conditional formatting for exceeded values
                    subjectList.forEach((subjectName, subIndex) => {
                        const exceededColIndex = 6 + (subIndex * 3) + 2; // Calculate exceeded column index
                        const exceededCell = row.getCell(exceededColIndex);
                        if (exceededCell.value && exceededCell.value > 0) {
                            exceededCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                            exceededCell.fill = {
                                type: 'pattern',
                                pattern: 'solid',
                                fgColor: { argb: 'FFDC3545' }
                            };
                        }
                    });
                    
                    // Highlight Total Exceeded column
                    const totalExceededCell = row.getCell(5);
                    if (totalExceededCell.value && totalExceededCell.value > 0) {
                        totalExceededCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                        totalExceededCell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: { argb: 'FFFF6B6B' }
                        };
                    }
                });

                // Freeze first row (header)
                worksheet.views = [
                    { state: 'frozen', xSplit: 0, ySplit: 1 }
                ];

                // Add auto-filter to header row
                worksheet.autoFilter = {
                    from: { row: 1, column: 1 },
                    to: { row: 1, column: columns.length }
                };

                const buffer = await workbook.xlsx.writeBuffer();
                fileDownload(buffer, `Exceeded_Threshold_${new Date().getTime()}.xlsx`);
                toast.success('Export completed successfully');
            } else {
                setLoading(false);
                toast.error(resp?.errors[0]?.msg || 'Failed to export data');
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal server error');
        }
    };

    const columns = [
        {
            name: 'Franchise',
            selector: (row) => row.name,
            sortable: true,
            sortField: 'franchise_name',
            wrap: true,
            minWidth: '200px'
        },
        // {
        //     name: 'Kashflow Order Account',
        //     selector: (row) => row.kashflow_order_account ?? '---',
        //     sortable: true,
        //     sortField: 'kashflow_order_account',
        //     wrap: true,
        //     minWidth: '180px'
        // },
        {
            name: 'Month',
            selector: (row) => row.month,
            sortable: true,
            sortField: 'month',
            format: (row) => {
                return (months.find(obj => row.month === obj.id))?.name ?? '---';
            },
            minWidth: '120px'
        },
        {
            name: 'Year',
            selector: (row) => row.year,
            sortable: true,
            sortField: 'year',
            minWidth: '100px'
        },
        {
            name: (
                <span title="Total worksheets printed for all students (both exceeded and non-exceeded)">
                    Total Printed <i className="fas fa-info-circle" style={{ fontSize: '11px', color: '#6c757d', marginLeft: '4px' }}></i>
                </span>
            ),
            selector: (row) => row.worksheets,
            sortable: true,
            sortField: 'worksheets',
            format: (row) => <b>{row.worksheets}</b>,
            center: true,
            minWidth: '150px'
        },
        {
            name: 'Total Exceeded',
            selector: (row) => row.total_exceeded,
            sortable: true,
            sortField: 'total_exceeded',
            format: (row) => (
                <span style={{ 
                    backgroundColor: '#dc3545', 
                    color: '#fff', 
                    padding: '4px 12px', 
                    borderRadius: '4px',
                    fontWeight: 'bold'
                }}>
                    +{row.total_exceeded}
                </span>
            ),
            center: true,
            minWidth: '150px'
        },
        {
            name: 'Action',
            cell: (row) => (
                <>
                    {canAccess && (
                        <a href='#' onClick={(e) => { viewDetails(e, row) }} title="View Details">
                            <i className="fas fa-eye mr-2"></i>
                        </a>
                    )}
                </>
            ),
            center: true,
            minWidth: '100px'
        }
    ];

    const ExpandedComponent = ({ data }) => {
        return (
            <div style={{ padding: '20px 30px', backgroundColor: '#f8f9fa', borderTop: '2px solid #dee2e6' }}>
                <h6 style={{ marginBottom: '20px', fontWeight: 'bold', color: '#495057', fontSize: '15px' }}>
                    <i className="fas fa-list-ul mr-2"></i>Subject-wise Threshold Details
                </h6>
                <div style={{ overflowX: 'auto' }}>
                    <table className="table table-bordered table-sm" style={{ marginBottom: 0 }}>
                        <thead style={{ backgroundColor: '#e9ecef' }}>
                            <tr>
                                <th style={{ fontWeight: 'bold', fontSize: '13px', padding: '12px' }}>Subject</th>
                                <th style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '13px', padding: '12px' }}>Worksheets Printed</th>
                                <th style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '13px', padding: '12px' }}>Threshold</th>
                                <th style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '13px', padding: '12px' }}>Exceeded By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.subjects && data.subjects.length > 0 ? (
                                data.subjects.map((subject, index) => (
                                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                                        <td style={{ padding: '12px', fontSize: '14px' }}>
                                            <i className="fas fa-book mr-2" style={{ color: '#6c757d' }}></i>
                                            <b>{subject.subject_name}</b>
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '12px', fontSize: '14px' }}>
                                            <span style={{ 
                                                backgroundColor: '#e3f2fd', 
                                                padding: '4px 12px', 
                                                borderRadius: '4px',
                                                fontWeight: 'bold'
                                            }}>
                                                {subject.worksheet_count}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '12px', fontSize: '14px' }}>
                                            <span style={{ 
                                                backgroundColor: '#ffebee', 
                                                color: '#dc3545', 
                                                padding: '4px 12px', 
                                                borderRadius: '4px',
                                                fontWeight: 'bold'
                                            }}>
                                                {subject.threshold}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '12px', fontSize: '14px' }}>
                                            <span style={{ 
                                                backgroundColor: '#dc3545', 
                                                color: '#fff', 
                                                padding: '4px 12px', 
                                                borderRadius: '4px',
                                                fontWeight: 'bold'
                                            }}>
                                                +{subject.exceeded_count}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={4} style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
                                        No subject data available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const customStyles: TableStyles = {
        head: {
            style: {
                color: "#5E5873",
                fontSize: "13px",
                fontWeight: 900,
            },
        },
        headCells: {
            style: {
                borderColor: "#E9ECEF",
                textTransform: "uppercase",
                letterSpacing: "1px",
                fontSize: '14px',
                fontWeight: 'bold'
            }
        },
        headRow: {
            style: {
                backgroundColor: "#F7F7F8",
                minHeight: "44px",
                fontWeight: 500,
            },
        },
    };

    return (
        <div>
            <section className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1>Exceeded Print Threshold</h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item"><a href="/">Home</a></li>
                                <li className="breadcrumb-item">Print Summary</li>
                                <li className="breadcrumb-item active">Exceeded Threshold</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </section>

            <section className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">Exceeded Print Threshold Records</h3>
                                </div>
                                <div className="card-body">
                                    <div className="row mb-3">
                                        <div className="col-sm-3">
                                            <Select
                                                ref={selectRef}
                                                name="franchiseId"
                                                placeholder="Search By Franchise"
                                                options={franchises}
                                                getOptionValue={(option)=>`${option['id']}`}
                                                getOptionLabel={(option)=> option['name'] }
                                                onChange={ (option) => {onChangeFranchise(option)}}
                                                isClearable={true}
                                                className="basic-single"
                                                classNamePrefix="select"
                                            />
                                        </div>
                                        <div className="col-sm-3">
                                            <select
                                                className="form-control"
                                                name="month"
                                                onChange={(e) => setSearch({ ...search, month: e.target.value })} 
                                                value={search.month}
                                            >
                                                <option value="">All Months</option>
                                                {months.map((obj) => (
                                                    <option key={obj.name} value={obj.id}>
                                                        {obj.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-sm-3">
                                            <select
                                                className="form-control"
                                                name="year"
                                                onChange={(e) => setSearch({ ...search, year: e.target.value })} 
                                                value={search.year}
                                            >
                                                <option value="">All Years</option>
                                                {years.map((year) => (
                                                    <option key={year} value={year}>
                                                        {year}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className='col-sm-3'>
                                            <Button type="button" isLoading={loading} onClick={handleResetFilter} className="btn btn-warning mr-2">Reset Filter</Button>
                                            <Button type="button" isLoading={loading} onClick={handleExport} className="btn btn-success"><i className="far fa-file-excel mr-1"></i> Export</Button>
                                        </div>
                                    </div>

                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        progressPending={loading}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={totalRows}
                                        paginationDefaultPage={currentPage}
                                        onChangeRowsPerPage={handlePerRowsChange}
                                        onChangePage={handlePageChange}
                                        paginationPerPage={perPage}
                                        paginationRowsPerPageOptions={[5, 10, 20, 30, 50, 100]}
                                        onSort={handleSort}
                                        sortServer
                                        customStyles={customStyles}
                                        highlightOnHover
                                        striped
                                        expandableRows
                                        expandableRowsComponent={ExpandedComponent}
                                        expandOnRowClicked
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ExceededThreshold;
